import axios, { AxiosResponse } from 'axios'
import { getAdminToken, useAuthStore } from 'shared/store/authStore'

function getApiBase(): string {
  const env = (import.meta.env.VITE_API_URL ?? '').trim()
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return ''
  }
  return env
}
const API_URL = getApiBase()

export const api = axios.create({
  baseURL: API_URL ? `${API_URL}/api/v1` : '/api/v1',
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config) => {
  const token = getAdminToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

api.interceptors.response.use(
  (r: AxiosResponse) => r,
  (err) => {
    if (err.response?.status === 401) {
      useAuthStore.getState().logoutCustomer()
      window.location.href = '/admin/login'
    }
    return Promise.reject(err)
  },
)

export type AdminLoginResponse = {
  access_token?: string
  token_type?: string
  user_id?: string
  email?: string
  role?: string
  requires_2fa?: boolean
  temp_token?: string
}

export const adminAuth = {
  login: (email: string, password: string) =>
    api.post<AdminLoginResponse>('/admin/login', { email, password }),
  verify2fa: (tempToken: string, code: string) =>
    api.post<AdminLoginResponse>('/admin/login/verify-2fa', { temp_token: tempToken, code }),
  me: () =>
    api.get<{
      id: string
      email: string
      role: string
      totp_enabled?: boolean
      panels?: ('customer' | 'company' | 'admin')[]
    }>('/admin/me'),
  twoFaStatus: () => api.get<{ enabled: boolean }>('/admin/2fa/status'),
  twoFaSetup: () => api.post<{ secret: string; provisioning_uri: string }>('/admin/2fa/setup'),
  twoFaVerify: (code: string) => api.post('/admin/2fa/verify', { code }),
  twoFaDisable: () => api.post('/admin/2fa/disable'),
}

export const adminCompanies = {
  list: (params?: { status?: string; is_verified?: boolean; search?: string; skip?: number; limit?: number }) =>
    api.get<{ items: unknown[]; total: number }>('/admin/companies', { params }),
  pending: () => api.get<unknown[]>('/admin/companies/pending'),
  get: (id: string) => api.get(`/admin/companies/${id}`),
  approve: (id: string, note?: string) => api.post(`/admin/companies/${id}/approve`, { note }),
  reject: (id: string, reason: string) =>
    api.post(`/admin/companies/${id}/reject`, { reason, confirmed: true }),
  suspend: (id: string) => api.post(`/admin/companies/${id}/suspend`, { confirmed: true }),
  activate: (id: string) => api.post(`/admin/companies/${id}/activate`),
  update: (id: string, data: { name?: string; address?: string; is_active?: boolean }) =>
    api.patch(`/admin/companies/${id}`, data),
  bulkApprove: (companyIds: string[], note?: string) =>
    api.post<{ approved: number; errors: { id: string; error: string }[] }>('/admin/companies/bulk-approve', {
      company_ids: companyIds,
      note,
    }),
  reviewHistory: (companyId: string) =>
    api.get<{ id: string; user_id: string | null; action: string; changes: unknown; ip_address: string | null; timestamp: string }[]>(
      `/admin/companies/${companyId}/review-history`
    ),
}

export const adminPayments = {
  list: (params?: { status?: string; company_id?: string; skip?: number; limit?: number }) =>
    api.get<{ items: unknown[]; total: number }>('/admin/payments', { params }),
  flagged: () => api.get<unknown[]>('/admin/payments/flagged'),
  get: (id: string) => api.get(`/admin/payments/${id}`),
  refund: (id: string, reason?: string) =>
    api.post(`/admin/payments/${id}/refund`, { reason, confirmed: true }),
  exportCsv: (params?: { status?: string; company_id?: string }) =>
    api.get<Blob>('/admin/payments/export', { params, responseType: 'blob' }),
}

export const adminPayouts = {
  list: (params?: { status?: string; company_id?: string; skip?: number; limit?: number }) =>
    api.get<{ items: unknown[]; total: number }>('/admin/payouts', { params }),
  trigger: (company_id: string, period_start: string, period_end: string, reference?: string) =>
    api.post('/admin/payouts/trigger', { company_id, period_start, period_end, reference }),
  process: (id: string) => api.post(`/admin/payouts/${id}/process`),
  complete: (id: string) => api.post(`/admin/payouts/${id}/complete`),
  fail: (id: string, reason?: string) =>
    api.post(`/admin/payouts/${id}/fail`, { reason, confirmed: true }),
  retry: (id: string) => api.post(`/admin/payouts/${id}/retry`),
}

export const adminAnalytics = {
  dashboard: () =>
    api.get<{
      total_companies: number
      pending_registrations: number
      active_companies: number
      suspended_companies?: number
      total_transactions: number
      total_volume: string
      transactions_today: number
      volume_today: string
      transactions_week?: number
      volume_week?: string
      transactions_month?: number
      volume_month?: string
      platform_fees_collected?: string
      payment_success_rate_today?: number | null
      recent_activity?: { type: string; description: string; entity_id?: string; entity_type?: string; timestamp: string }[]
    }>('/admin/analytics/dashboard'),
  revenue: (days?: number) =>
    api.get<{ date: string; transactions: number; volume: number }[]>('/admin/analytics/revenue', {
      params: { days },
    }),
  growth: () =>
    api.get<{ company_signups: { date: string; count: number }[] }>('/admin/analytics/growth'),
  recentActivity: () =>
    api.get<{
      new_registrations: { id: string; name: string; tin: string; created_at: string }[]
      high_value_transactions: { id: string; amount: number; currency: string; created_at: string }[]
      failed_payments: { id: string; amount: number; created_at: string }[]
    }>('/admin/analytics/recent-activity'),
  successRate: (days?: number) =>
    api.get<{ date: string; total: number; completed: number; success_rate: number }[]>(
      '/admin/analytics/success-rate',
      { params: { days } }
    ),
  financialReport: (days?: number) =>
    api.get<{
      period_days: number
      transactions: { count: number; volume: number }
      payouts: { count: number; net_amount: number; platform_fees_collected: number }
    }>('/admin/analytics/reports/financial', { params: { days } }),
}

export const adminUsers = {
  platform: (params?: { role?: string; skip?: number; limit?: number }) =>
    api.get<{ items: { id: string; email: string; role: string; is_active: string; must_change_password?: string; created_at: string }[]; total: number }>(
      '/admin/users/platform',
      { params }
    ),
  company: (params?: { company_id?: string; skip?: number; limit?: number }) =>
    api.get<{
      items: { id: string; email: string; role: string; company_id?: string; company_name?: string; is_active: string; created_at: string }[]
      total: number
    }>('/admin/users/company', { params }),
  suspend: (id: string) => api.post(`/admin/users/${id}/suspend`),
  activate: (id: string) => api.post(`/admin/users/${id}/activate`),
  forcePasswordReset: (id: string) =>
    api.post(`/admin/users/${id}/force-password-reset`, { confirmed: true }),
}

export type DefaultFeesPayload = {
  bank_fee_percent?: number
  bank_fee_fixed?: number
  platform_fee_percent?: number
  platform_fee_fixed?: number
}

export const adminConfig = {
  getDefaultFees: () =>
    api.get<{
      bank_fee_percent?: number
      bank_fee_fixed?: number
      platform_fee_percent?: number
      platform_fee_fixed?: number
      bank_fee_type?: string
      bank_fee_value?: number
      platform_fee_type?: string
      platform_fee_tiers?: unknown
    }>('/admin/config/fees/default'),
  updateDefaultFees: (data: DefaultFeesPayload) =>
    api.patch<{ ok: boolean }>('/admin/config/fees/default', data),
  getCompanyFees: (companyId: string) =>
    api.get<Record<string, unknown>>(`/admin/config/fees/company/${companyId}`),
  updateCompanyFees: (companyId: string, data: Record<string, unknown>) =>
    api.patch(`/admin/config/fees/companies/${companyId}`, data),
  getSettings: () =>
    api.get<{
      payout_schedule: string
      current_period_start?: string
      current_period_end?: string
      next_payout_date?: string
      session_timeout_minutes: number
      ip_whitelist_configured?: boolean
      ip_whitelist_enabled?: boolean
      allow_duplicate_receipt_payment: boolean
    }>('/admin/config/settings'),
  updateSettings: (data: { allow_duplicate_receipt_payment?: boolean }) =>
    api.patch<{ ok: boolean }>('/admin/config/settings', data),
  listInvoiceVerificationDomains: () =>
    api.get<{ items: { id: string; domain: string }[] }>('/admin/config/invoice-verification-domains'),
  addInvoiceVerificationDomain: (domain: string) =>
    api.post<{ id: string; domain: string }>('/admin/config/invoice-verification-domains', { domain }),
  removeInvoiceVerificationDomain: (id: string) =>
    api.delete(`/admin/config/invoice-verification-domains/${id}`),
  getPaymentProvider: () =>
    api.get<{
      provider: string
      monri_api_key_masked: string
      monri_api_secret_masked: string
      monri_configured: boolean
    }>('/admin/config/payment-provider'),
  updatePaymentProvider: (data: {
    provider: string
    monri_api_key?: string
    monri_api_secret?: string
  }) => api.patch<{ ok: boolean }>('/admin/config/payment-provider', data),
}

export const adminAudit = {
  list: (params?: { user_id?: string; action?: string; entity_type?: string; skip?: number; limit?: number }) =>
    api.get<{ items: unknown[]; total: number }>('/admin/audit', { params }),
  exportCsv: (params?: { user_id?: string; action?: string; entity_type?: string; limit?: number }) =>
    api.get('/admin/audit/export', { params, responseType: 'blob' }),
}
